import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, Plus, Package, AlertTriangle, TrendingDown, TrendingUp,
  ArrowDownCircle, ArrowUpCircle, Eye, Edit2, Box, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const InventoryPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    total_productos: 0, total_productos_activos: 0, productos_stock_bajo: 0,
    productos_sin_stock: 0, valor_total_inventario: 0, total_movimientos_mes: 0,
    entradas_mes: 0, salidas_mes: 0
  });
  const [products, setProducts] = useState([]);
  const [constants, setConstants] = useState({ categories: [], units: [], movement_types: [], entry_reasons: [], exit_reasons: [] });
  const [suppliers, setSuppliers] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStock, setFilterStock] = useState('');

  // Modals
  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showKardexModal, setShowKardexModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [kardexData, setKardexData] = useState([]);

  // Forms
  const [newProduct, setNewProduct] = useState({
    codigo: '', nombre_comercial: '', nombre_generico: '', presentacion: '',
    categoria: 'medicamento', unidad_medida: 'unidad', precio_costo: '', precio_venta: '',
    stock_minimo: 5, stock_maximo: '', ubicacion_almacen: '', proveedor_default_id: '', 
    requiere_receta: false, notas: ''
  });

  const [editProduct, setEditProduct] = useState({
    id: '', codigo: '', nombre_comercial: '', nombre_generico: '', presentacion: '',
    categoria: 'medicamento', unidad_medida: 'unidad', precio_costo: '', precio_venta: '',
    stock_minimo: 5, stock_maximo: '', ubicacion_almacen: '', proveedor_default_id: '', 
    requiere_receta: false, notas: ''
  });

  const [newMovement, setNewMovement] = useState({
    producto_id: '', tipo: 'entrada', cantidad: '', costo_unitario: '',
    motivo: '', proveedor_id: '', lote: '', fecha_vencimiento: '', notas: ''
  });

  // Fetch data
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inventory/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/inventory/products?`;
      if (filterCategory && filterCategory !== 'todas') url += `categoria=${filterCategory}&`;
      if (filterStock === 'bajo') url += `stock_bajo=true&`;
      if (filterStock === 'sin') url += `sin_stock=true&`;
      if (searchQuery) url += `search=${searchQuery}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filterCategory, filterStock, searchQuery]);

  const fetchConstants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inventory/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Error fetching constants:', error);
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/suppliers/search/quick?q=&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, [token]);

  const fetchLowStockAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inventory/alerts/low-stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLowStockAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    fetchConstants();
    fetchSuppliers();
    fetchLowStockAlerts();
  }, [fetchStats, fetchConstants, fetchSuppliers, fetchLowStockAlerts]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Create product
  const handleCreateProduct = async () => {
    if (!newProduct.codigo || !newProduct.nombre_comercial) {
      toast.error('Código y nombre son requeridos');
      return;
    }
    try {
      // Clean up the product data before sending
      const productData = {
        codigo: newProduct.codigo,
        nombre_comercial: newProduct.nombre_comercial,
        nombre_generico: newProduct.nombre_generico || null,
        presentacion: newProduct.presentacion || null,
        categoria: newProduct.categoria || 'medicamento',
        unidad_medida: newProduct.unidad_medida || 'unidad',
        precio_costo: newProduct.precio_costo ? parseFloat(newProduct.precio_costo) : null,
        precio_venta: newProduct.precio_venta ? parseFloat(newProduct.precio_venta) : null,
        stock_minimo: parseInt(newProduct.stock_minimo) || 0,
        stock_maximo: newProduct.stock_maximo ? parseInt(newProduct.stock_maximo) : null,
        ubicacion_almacen: newProduct.ubicacion_almacen || null,
        proveedor_default_id: newProduct.proveedor_default_id || null,
        requiere_receta: newProduct.requiere_receta || false,
        notas: newProduct.notas || null
      };
      
      await axios.post(`${API}/inventory/products`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Producto creado exitosamente');
      setShowNewProductModal(false);
      setNewProduct({
        codigo: '', nombre_comercial: '', nombre_generico: '', presentacion: '',
        categoria: 'medicamento', unidad_medida: 'unidad', precio_costo: '', precio_venta: '',
        stock_minimo: 5, stock_maximo: '', ubicacion_almacen: '', proveedor_default_id: '',
        requiere_receta: false, notas: ''
      });
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.detail || 'Error al crear producto');
    }
  };

  // Update product
  const handleUpdateProduct = async () => {
    if (!editProduct.codigo || !editProduct.nombre_comercial) {
      toast.error('Código y nombre son requeridos');
      return;
    }
    try {
      const productData = {
        codigo: editProduct.codigo,
        nombre_comercial: editProduct.nombre_comercial,
        nombre_generico: editProduct.nombre_generico || null,
        presentacion: editProduct.presentacion || null,
        categoria: editProduct.categoria || 'medicamento',
        unidad_medida: editProduct.unidad_medida || 'unidad',
        precio_costo: editProduct.precio_costo ? parseFloat(editProduct.precio_costo) : null,
        precio_venta: editProduct.precio_venta ? parseFloat(editProduct.precio_venta) : null,
        stock_minimo: parseInt(editProduct.stock_minimo) || 0,
        stock_maximo: editProduct.stock_maximo ? parseInt(editProduct.stock_maximo) : null,
        ubicacion_almacen: editProduct.ubicacion_almacen || null,
        proveedor_default_id: editProduct.proveedor_default_id || null,
        requiere_receta: editProduct.requiere_receta || false,
        notas: editProduct.notas || null
      };
      
      await axios.put(`${API}/inventory/products/${editProduct.id}`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Producto actualizado');
      setShowEditProductModal(false);
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar producto');
    }
  };

  // Delete product
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Está seguro de desactivar este producto?')) return;
    try {
      await axios.delete(`${API}/inventory/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Producto desactivado');
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desactivar producto');
    }
  };

  // Open edit modal
  const openEditModal = (product) => {
    setEditProduct({
      id: product.id,
      codigo: product.codigo || '',
      nombre_comercial: product.nombre_comercial || '',
      nombre_generico: product.nombre_generico || '',
      presentacion: product.presentacion || '',
      categoria: product.categoria || 'medicamento',
      unidad_medida: product.unidad_medida || 'unidad',
      precio_costo: product.precio_costo || '',
      precio_venta: product.precio_venta || '',
      stock_minimo: product.stock_minimo || 5,
      stock_maximo: product.stock_maximo || '',
      ubicacion_almacen: product.ubicacion_almacen || '',
      proveedor_default_id: product.proveedor_default_id || '',
      requiere_receta: product.requiere_receta || false,
      notas: product.notas || ''
    });
    setShowEditProductModal(true);
  };

  // Create movement
  const handleCreateMovement = async () => {
    if (!newMovement.producto_id || !newMovement.cantidad || !newMovement.motivo) {
      toast.error('Producto, cantidad y motivo son requeridos');
      return;
    }
    if (newMovement.tipo === 'entrada' && !newMovement.costo_unitario) {
      toast.error('El costo unitario es requerido para entradas');
      return;
    }
    try {
      await axios.post(`${API}/inventory/movements`, {
        ...newMovement,
        cantidad: parseInt(newMovement.cantidad),
        costo_unitario: newMovement.costo_unitario ? parseFloat(newMovement.costo_unitario) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Movimiento registrado');
      setShowMovementModal(false);
      setNewMovement({
        producto_id: '', tipo: 'entrada', cantidad: '', costo_unitario: '',
        motivo: '', proveedor_id: '', lote: '', fecha_vencimiento: '', notas: ''
      });
      fetchProducts();
      fetchStats();
      fetchLowStockAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  // View Kardex
  const handleViewKardex = async (product) => {
    setSelectedProduct(product);
    try {
      const response = await axios.get(`${API}/inventory/products/${product.id}/kardex`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKardexData(response.data);
      setShowKardexModal(true);
    } catch (error) {
      toast.error('Error al cargar kardex');
    }
  };

  // Open movement modal for specific product
  const openMovementForProduct = (product, tipo) => {
    setNewMovement({
      producto_id: product.id, tipo: tipo, cantidad: '', costo_unitario: '',
      motivo: '', proveedor_id: product.proveedor_default_id || '', 
      lote: '', fecha_vencimiento: '', notas: ''
    });
    setSelectedProduct(product);
    setShowMovementModal(true);
  };

  const formatCurrency = (value) => `Q ${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-slate-900">Inventario</h1>
          <p className="text-slate-500 text-sm">Gestión de medicamentos e insumos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowMovementModal(true)}>
            <ArrowDownCircle className="w-4 h-4 mr-2" />
            Registrar Movimiento
          </Button>
          <Button onClick={() => setShowNewProductModal(true)} className="bg-[#0891B2] hover:bg-[#0e7490] text-white">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Producto
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-display font-medium text-slate-900">{stats.total_productos_activos}</div>
                <div className="text-sm text-slate-500">Productos activos</div>
              </div>
              <Package className="w-10 h-10 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-display font-medium text-amber-700">{stats.productos_stock_bajo}</div>
                <div className="text-sm text-amber-600">Stock bajo</div>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-display font-medium text-red-700">{stats.productos_sin_stock}</div>
                <div className="text-sm text-red-600">Sin stock</div>
              </div>
              <Box className="w-10 h-10 text-red-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-teal-200 bg-teal-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-display font-medium text-teal-700">{formatCurrency(stats.valor_total_inventario)}</div>
                <div className="text-sm text-teal-600">Valor total</div>
              </div>
              <TrendingUp className="w-10 h-10 text-teal-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alerts */}
      {lowStockAlerts.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5" />
              Alertas de Stock Bajo ({lowStockAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {lowStockAlerts.slice(0, 5).map(alert => (
                <Badge key={alert.producto_id} variant="outline" className="border-amber-400 text-amber-800 py-1 px-3">
                  {alert.codigo} - {alert.nombre_comercial}: {alert.cantidad_actual}/{alert.stock_minimo}
                </Badge>
              ))}
              {lowStockAlerts.length > 5 && (
                <Badge variant="outline" className="border-amber-400 text-amber-800">
                  +{lowStockAlerts.length - 5} más
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-4 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por código o nombre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-products"
          />
        </div>
        <Select value={filterCategory || "todas"} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48" data-testid="filter-category">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las categorías</SelectItem>
            {(constants.categories || []).map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStock || "todos"} onValueChange={setFilterStock}>
          <SelectTrigger className="w-40" data-testid="filter-stock">
            <SelectValue placeholder="Stock" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todo el stock</SelectItem>
            <SelectItem value="bajo">Stock bajo</SelectItem>
            <SelectItem value="sin">Sin stock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Código</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Producto</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Categoría</th>
                <th className="text-right p-4 text-sm font-medium text-slate-600">Stock</th>
                <th className="text-right p-4 text-sm font-medium text-slate-600">P. Costo</th>
                <th className="text-right p-4 text-sm font-medium text-slate-600">P. Venta</th>
                <th className="text-right p-4 text-sm font-medium text-slate-600">Valor Inv.</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-500">Cargando...</td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-500">No hay productos</td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className={`border-b hover:bg-slate-50 ${product.alerta_stock_bajo ? 'bg-amber-50' : ''}`}>
                    <td className="p-4">
                      <span className="font-mono text-sm text-slate-700">{product.codigo}</span>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{product.nombre_comercial}</div>
                      {product.presentacion && (
                        <div className="text-xs text-slate-500">{product.presentacion}</div>
                      )}
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="text-slate-600">
                        {product.categoria_label}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      <div className={`font-medium ${product.alerta_stock_bajo ? 'text-amber-700' : product.cantidad_actual === 0 ? 'text-red-700' : 'text-slate-900'}`}>
                        {product.cantidad_actual} {product.unidad_medida}
                      </div>
                      <div className="text-xs text-slate-500">Mín: {product.stock_minimo}</div>
                    </td>
                    <td className="p-4 text-right">
                      <span className="text-slate-700">{formatCurrency(product.precio_costo || product.precio_costo_promedio)}</span>
                    </td>
                    <td className="p-4 text-right">
                      {product.precio_venta ? (
                        <div>
                          <span className="font-medium text-green-700">{formatCurrency(product.precio_venta)}</span>
                          {product.margen_ganancia && (
                            <div className="text-xs text-green-600">+{product.margen_ganancia}%</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <span className="font-medium text-slate-900">{formatCurrency(product.valor_inventario)}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openMovementForProduct(product, 'entrada')}
                          className="text-green-600 hover:bg-green-50"
                          title="Entrada"
                        >
                          <TrendingUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openMovementForProduct(product, 'salida')}
                          className="text-red-600 hover:bg-red-50"
                          title="Salida"
                          disabled={product.cantidad_actual === 0}
                        >
                          <TrendingDown className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewKardex(product)}
                          title="Ver Kardex"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(product)}
                          className="text-blue-600 hover:bg-blue-50"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteProduct(product.id)}
                          className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                          title="Desactivar"
                          disabled={product.cantidad_actual > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* New Product Modal */}
      <Dialog open={showNewProductModal} onOpenChange={setShowNewProductModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Nuevo Producto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input
                  value={newProduct.codigo}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                  placeholder="MED-001"
                  data-testid="product-code"
                />
              </div>
              <div>
                <Label>Nombre comercial *</Label>
                <Input
                  value={newProduct.nombre_comercial}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, nombre_comercial: e.target.value }))}
                  placeholder="Nombre del producto"
                  data-testid="product-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre genérico</Label>
                <Input
                  value={newProduct.nombre_generico}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, nombre_generico: e.target.value }))}
                  placeholder="Principio activo"
                />
              </div>
              <div>
                <Label>Presentación</Label>
                <Input
                  value={newProduct.presentacion}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, presentacion: e.target.value }))}
                  placeholder="500mg x 30 tabletas"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Categoría</Label>
                <Select 
                  value={newProduct.categoria || 'medicamento'} 
                  onValueChange={(v) => setNewProduct(prev => ({ ...prev, categoria: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(constants.categories || []).length > 0 ? (
                      (constants.categories || []).map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="medicamento">Medicamento</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unidad de medida</Label>
                <Select 
                  value={newProduct.unidad_medida || 'unidad'} 
                  onValueChange={(v) => setNewProduct(prev => ({ ...prev, unidad_medida: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(constants.units || []).length > 0 ? (
                      (constants.units || []).map(unit => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="unidad">Unidad</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Proveedor default</Label>
                <Select 
                  value={newProduct.proveedor_default_id || "ninguno"} 
                  onValueChange={(v) => setNewProduct(prev => ({ ...prev, proveedor_default_id: v === "ninguno" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninguno">Sin proveedor</SelectItem>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.nombre_comercial}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Stock mínimo</Label>
                <Input
                  type="number"
                  value={newProduct.stock_minimo}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, stock_minimo: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Stock máximo</Label>
                <Input
                  type="number"
                  value={newProduct.stock_maximo}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, stock_maximo: e.target.value ? parseInt(e.target.value) : '' }))}
                  placeholder="Opcional"
                />
              </div>
              <div>
                <Label>Ubicación</Label>
                <Input
                  value={newProduct.ubicacion_almacen}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, ubicacion_almacen: e.target.value }))}
                  placeholder="Estante A-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Precio Costo (Q)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newProduct.precio_costo}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, precio_costo: e.target.value }))}
                  placeholder="0.00"
                  data-testid="product-cost-price"
                />
              </div>
              <div>
                <Label>Precio Venta (Q)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newProduct.precio_venta}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, precio_venta: e.target.value }))}
                  placeholder="0.00"
                  data-testid="product-sale-price"
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={newProduct.notas}
                onChange={(e) => setNewProduct(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Observaciones adicionales..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProductModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateProduct} className="bg-[#0891B2] hover:bg-[#0e7490] text-white">
              Crear Producto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Modal */}
      <Dialog open={showEditProductModal} onOpenChange={setShowEditProductModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Editar Producto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input
                  value={editProduct.codigo}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                  placeholder="MED-001"
                />
              </div>
              <div>
                <Label>Nombre comercial *</Label>
                <Input
                  value={editProduct.nombre_comercial}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, nombre_comercial: e.target.value }))}
                  placeholder="Nombre del producto"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre genérico</Label>
                <Input
                  value={editProduct.nombre_generico}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, nombre_generico: e.target.value }))}
                  placeholder="Principio activo"
                />
              </div>
              <div>
                <Label>Presentación</Label>
                <Input
                  value={editProduct.presentacion}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, presentacion: e.target.value }))}
                  placeholder="500mg x 30 tabletas"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Categoría</Label>
                <Select 
                  value={editProduct.categoria || 'medicamento'} 
                  onValueChange={(v) => setEditProduct(prev => ({ ...prev, categoria: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(constants.categories || []).length > 0 ? (
                      (constants.categories || []).map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="medicamento">Medicamento</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unidad de medida</Label>
                <Select 
                  value={editProduct.unidad_medida || 'unidad'} 
                  onValueChange={(v) => setEditProduct(prev => ({ ...prev, unidad_medida: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(constants.units || []).length > 0 ? (
                      (constants.units || []).map(unit => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="unidad">Unidad</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Proveedor default</Label>
                <Select 
                  value={editProduct.proveedor_default_id || "ninguno"} 
                  onValueChange={(v) => setEditProduct(prev => ({ ...prev, proveedor_default_id: v === "ninguno" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninguno">Sin proveedor</SelectItem>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.nombre_comercial}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Stock mínimo</Label>
                <Input
                  type="number"
                  value={editProduct.stock_minimo}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, stock_minimo: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Stock máximo</Label>
                <Input
                  type="number"
                  value={editProduct.stock_maximo}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, stock_maximo: e.target.value ? parseInt(e.target.value) : '' }))}
                  placeholder="Opcional"
                />
              </div>
              <div>
                <Label>Ubicación</Label>
                <Input
                  value={editProduct.ubicacion_almacen}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, ubicacion_almacen: e.target.value }))}
                  placeholder="Estante A-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Precio Costo (Q)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editProduct.precio_costo}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, precio_costo: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Precio Venta (Q)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editProduct.precio_venta}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, precio_venta: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={editProduct.notas}
                onChange={(e) => setEditProduct(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Observaciones adicionales..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProductModal(false)}>Cancelar</Button>
            <Button onClick={handleUpdateProduct} className="bg-[#0891B2] hover:bg-[#0e7490] text-white">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Modal */}
      <Dialog open={showMovementModal} onOpenChange={setShowMovementModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">
              {newMovement.tipo === 'entrada' ? 'Entrada de Inventario' : 
               newMovement.tipo === 'salida' ? 'Salida de Inventario' : 'Movimiento de Inventario'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!selectedProduct && (
              <div>
                <Label>Producto *</Label>
                <Select 
                  value={newMovement.producto_id} 
                  onValueChange={(v) => {
                    const prod = products.find(p => p.id === v);
                    setNewMovement(prev => ({ 
                      ...prev, 
                      producto_id: v,
                      proveedor_id: prod?.proveedor_default_id || ''
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar producto..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.codigo} - {p.nombre_comercial} (Stock: {p.cantidad_actual})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {selectedProduct && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="font-medium">{selectedProduct.codigo} - {selectedProduct.nombre_comercial}</div>
                <div className="text-sm text-slate-500">Stock actual: {selectedProduct.cantidad_actual} {selectedProduct.unidad_medida}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de movimiento</Label>
                <Select 
                  value={newMovement.tipo} 
                  onValueChange={(v) => setNewMovement(prev => ({ ...prev, tipo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(constants.movement_types || []).map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cantidad *</Label>
                <Input
                  type="number"
                  value={newMovement.cantidad}
                  onChange={(e) => setNewMovement(prev => ({ ...prev, cantidad: e.target.value }))}
                  min="1"
                  data-testid="movement-quantity"
                />
              </div>
            </div>
            {(newMovement.tipo === 'entrada' || newMovement.tipo === 'ajuste_positivo') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Costo unitario (Q) {newMovement.tipo === 'entrada' ? '*' : ''}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newMovement.costo_unitario}
                    onChange={(e) => setNewMovement(prev => ({ ...prev, costo_unitario: e.target.value }))}
                    placeholder="0.00"
                    data-testid="movement-cost"
                  />
                </div>
                <div>
                  <Label>Proveedor</Label>
                  <Select 
                    value={newMovement.proveedor_id || "ninguno"} 
                    onValueChange={(v) => setNewMovement(prev => ({ ...prev, proveedor_id: v === "ninguno" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ninguno">Sin proveedor</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.nombre_comercial}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div>
              <Label>Motivo *</Label>
              <Select 
                value={newMovement.motivo} 
                onValueChange={(v) => setNewMovement(prev => ({ ...prev, motivo: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar motivo..." />
                </SelectTrigger>
                <SelectContent>
                  {((newMovement.tipo === 'entrada' || newMovement.tipo === 'ajuste_positivo' 
                    ? constants.entry_reasons 
                    : constants.exit_reasons
                  ) || []).map(reason => (
                    <SelectItem key={reason} value={reason}>
                      {reason.replace(/_/g, ' ').charAt(0).toUpperCase() + reason.replace(/_/g, ' ').slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lote</Label>
                <Input
                  value={newMovement.lote}
                  onChange={(e) => setNewMovement(prev => ({ ...prev, lote: e.target.value }))}
                  placeholder="LOT-001"
                />
              </div>
              <div>
                <Label>Fecha vencimiento</Label>
                <Input
                  type="date"
                  value={newMovement.fecha_vencimiento}
                  onChange={(e) => setNewMovement(prev => ({ ...prev, fecha_vencimiento: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={newMovement.notas}
                onChange={(e) => setNewMovement(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Observaciones..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowMovementModal(false); setSelectedProduct(null); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateMovement} 
              className={newMovement.tipo === 'entrada' || newMovement.tipo === 'ajuste_positivo'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
              }
            >
              {newMovement.tipo === 'entrada' || newMovement.tipo === 'ajuste_positivo' ? 'Registrar Entrada' : 'Registrar Salida'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kardex Modal */}
      <Dialog open={showKardexModal} onOpenChange={setShowKardexModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              Kardex - {selectedProduct?.codigo} {selectedProduct?.nombre_comercial}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3">Fecha</th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">Motivo</th>
                  <th className="text-right p-3">Cantidad</th>
                  <th className="text-right p-3">Costo Unit.</th>
                  <th className="text-right p-3">Costo Total</th>
                  <th className="text-right p-3">Stock</th>
                  <th className="text-right p-3">Costo Prom.</th>
                </tr>
              </thead>
              <tbody>
                {kardexData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-500">Sin movimientos</td>
                  </tr>
                ) : (
                  kardexData.map((entry, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50">
                      <td className="p-3">{entry.fecha}</td>
                      <td className="p-3">
                        <Badge variant="outline" className={
                          entry.tipo === 'entrada' || entry.tipo === 'ajuste_positivo'
                            ? 'border-green-300 text-green-700'
                            : 'border-red-300 text-red-700'
                        }>
                          {entry.tipo_label}
                        </Badge>
                      </td>
                      <td className="p-3 text-slate-600">{entry.motivo.replace(/_/g, ' ')}</td>
                      <td className="p-3 text-right font-medium">
                        {entry.tipo === 'entrada' || entry.tipo === 'ajuste_positivo' ? '+' : '-'}{entry.cantidad}
                      </td>
                      <td className="p-3 text-right">{entry.costo_unitario ? formatCurrency(entry.costo_unitario) : '-'}</td>
                      <td className="p-3 text-right">{entry.costo_total ? formatCurrency(entry.costo_total) : '-'}</td>
                      <td className="p-3 text-right font-medium">{entry.stock_resultante}</td>
                      <td className="p-3 text-right">{formatCurrency(entry.costo_promedio)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
